/**
 * KAN-1000: вкладка "Цены": суперпользователь правит курс доллара и наценки,
 * по которым расход списывается с кошельков кабинетов. Сохраняются только
 * изменённые поля; ошибка 400 показывается текстом сервера, введённое не теряется.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePlatformPricing, useUpdatePlatformPricing } from '@/hooks/usePlatform';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { PRICING_FIELDS, buildPricingPatch, pricingToForm, type PricingForm } from '@/lib/platform';
import type { PlatformPricing, PlatformPricingField } from '@/services/api/platform';

const FIELD_LABELS: Record<PlatformPricingField, string> = {
  usd_rub_rate: 'Курс доллара, ₽',
  llm_markup: 'Наценка на токены (LLM)',
  voice_markup: 'Наценка на голос',
  telephony_rub_per_min: 'Телефония, ₽ за минуту',
};

/** Наценки-множители: значение меньше 1 = продажа ниже себестоимости. */
const MARKUP_FIELDS: PlatformPricingField[] = ['llm_markup', 'voice_markup'];

function isBelowCost(value: string): boolean {
  const num = Number(value.trim().replace(',', '.'));
  return value.trim() !== '' && Number.isFinite(num) && num < 1;
}

const FIELD_HINTS: Partial<Record<PlatformPricingField, string>> = {
  llm_markup: 'Множитель к себестоимости: 1.5 = плюс 50%',
  voice_markup: 'Множитель к себестоимости: 1.5 = плюс 50%. Пока не применяется',
  telephony_rub_per_min: 'Пусто: не задано. Пока не применяется',
};

export function PricingTab() {
  const { data, isLoading, isError } = usePlatformPricing();

  if (isLoading) return <p className="text-sm text-gray-500">Загрузка...</p>;
  if (isError || !data) {
    return (
      <p role="alert" className="text-sm text-red-600">
        Не удалось загрузить цены
      </p>
    );
  }
  // Ключ по updated_at: после сохранения форма берёт значения с сервера.
  return <PricingFormView key={data.updated_at} pricing={data} />;
}

function PricingFormView({ pricing }: { pricing: PlatformPricing }) {
  const initial = pricingToForm(pricing);
  const [form, setForm] = useState<PricingForm>(initial);
  const update = useUpdatePlatformPricing();
  const patch = buildPricingPatch(initial, form);
  const changed = Object.keys(patch).length > 0;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!changed) return;
    update.mutate(patch, { onSuccess: (saved) => setForm(pricingToForm(saved)) });
  };

  const error = update.isError ? extractErrorMessage(update.error, 'Не удалось сохранить цены') : null;

  return (
    <form onSubmit={submit} className="max-w-xl space-y-4 rounded-lg bg-white p-4 shadow sm:p-6">
      {PRICING_FIELDS.map((field) => (
        <div key={field} className="space-y-1">
          <Label htmlFor={`platform-pricing-${field}`}>{FIELD_LABELS[field]}</Label>
          <Input
            id={`platform-pricing-${field}`}
            data-testid={`platform-pricing-${field}`}
            inputMode="decimal"
            value={form[field]}
            onChange={(e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))}
          />
          {FIELD_HINTS[field] && <p className="text-xs text-gray-500">{FIELD_HINTS[field]}</p>}
          {MARKUP_FIELDS.includes(field) && isBelowCost(form[field]) && (
            <p data-testid={`platform-pricing-warning-${field}`} className="text-xs text-amber-700">
              Наценка меньше 1: продажа ниже себестоимости
            </p>
          )}
        </div>
      ))}
      {error && (
        <p role="alert" data-testid="platform-pricing-error" className="text-sm text-red-600">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" data-testid="platform-pricing-save" disabled={!changed || update.isPending}>
          Сохранить
        </Button>
        <span className="text-xs text-gray-500">
          Обновлено {new Date(pricing.updated_at).toLocaleString('ru-RU')}
        </span>
      </div>
    </form>
  );
}
